/**
 * Keeps OAuth/callback secrets out of telemetry and browser history (CWE-598).
 *
 * The Keycloak login callback delivers the authorization code in the URL
 * (`?code=...`). That URL must not be captured by Sentry tracing/replay (a
 * third-party SaaS) nor left sitting in browser history where it could be
 * replayed against the token endpoint.
 */

export const SENSITIVE_URL_PARAMS = ["code", "state", "session_state"];

/** Remove sensitive query params from a URL string, preserving everything else. */
export const scrubSensitiveUrl = (url?: string | null): string => {
  if (!url) return url ?? "";
  try {
    const parsed = new URL(url);
    let changed = false;
    SENSITIVE_URL_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.delete(param);
        changed = true;
      }
    });
    return changed ? parsed.toString() : url;
  } catch {
    // Relative or unparseable URL: strip the params textually as a fallback.
    return url
      .replace(new RegExp(`([?&])(${SENSITIVE_URL_PARAMS.join("|")})=[^&#]*`, "gi"), "$1")
      .replace(/([?&])&+/g, "$1")
      .replace(/[?&]$/, "");
  }
};

/**
 * Sentry beforeSend/beforeSendTransaction hook: scrub the request URL and any
 * navigation/http breadcrumb URLs so a captured event never carries the code.
 */
export const scrubSentryEvent = <T extends Record<string, any>>(event: T): T => {
  if (event?.request?.url) {
    event.request.url = scrubSensitiveUrl(event.request.url);
  }
  if (Array.isArray(event?.breadcrumbs)) {
    event.breadcrumbs.forEach((crumb: any) => {
      if (!crumb?.data) return;
      ["to", "from", "url"].forEach((key) => {
        if (typeof crumb.data[key] === "string") {
          crumb.data[key] = scrubSensitiveUrl(crumb.data[key]);
        }
      });
    });
  }
  return event;
};

/** Remove sensitive params from the current address bar without a reload/navigation. */
export const stripSensitiveParamsFromLocation = (): void => {
  const clean = scrubSensitiveUrl(window.location.href);
  if (clean !== window.location.href) {
    window.history.replaceState(window.history.state, document.title, clean);
  }
};

// OAuth code captured from the callback URL at app entry — BEFORE Sentry.init() and
// React mount — so the code can be stripped from the URL immediately (Session Replay
// records the pageload URL at init() and uploads outside the beforeSend hook, so
// scrubbing must happen before it starts) while the login flow can still consume it.
let capturedOAuthCode: string | null = null;

/**
 * Read the OAuth `code` from the current URL into memory, then strip the sensitive
 * params from the address bar. Call this ONCE at app entry, before Sentry.init() and
 * before React mounts, so Session Replay only ever records the cleaned URL.
 */
export const captureAndStripOAuthParams = (): void => {
  try {
    capturedOAuthCode = new URLSearchParams(window.location.search).get("code");
  } catch {
    capturedOAuthCode = null;
  }
  stripSensitiveParamsFromLocation();
};

/** The OAuth code captured at entry (null if the callback carried none). */
export const getCapturedOAuthCode = (): string | null => capturedOAuthCode;
